import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WorldProvider } from "./hooks/use-world";
import { Player } from "./actors/player";
import { Monster } from "./actors/monster";
import { Item } from "./actors/item";
import { Ladder } from "./actors/ladder";
import { NPC } from "./actors/npc";
import { HUD } from "./components/hud";
import { LadderChoice } from "./editor/ladder-choice";
import { Terminal } from "./components/terminal";
import { GameOver } from "./components/game-over";
import { useWorld } from "./hooks/use-world";
import { useGetQuery } from "hooks";
import { useStateValue, useSocket } from "providers";
import {
  updateDungeonSave,
  createDungeonSave,
  collectUniqueTreasure,
  recordMonsterKill,
} from "actions/api";
import { CharacterSelect, setActiveSave, getActiveSave } from "./components/character-select";
import { Canvas } from "./components/canvas";
import { useTileRegistry } from "./hooks/use-tile-registry";
import { useGameSocket } from "./hooks/use-game-socket";
import { gamePixelToLatLng, latLngToGamePixel } from "./lib/geo-projection";
import { Exit } from "./actors/exit";
import { GRID_SIZE } from "./config/constants";
import {
  loadPlayerPos,
  savePlayerPos,
  clearPlayerPos,
  loadPendingCollections,
  addPendingCollection,
  removePendingCollection,
} from "./lib/persistence";
import { computeStartPos } from "./lib/spawn";
import { LeafletMap, useMapInstance } from "components/Map";
import styled, { useTheme } from "styled-components";

const DEFAULT_LAT = 41.9168256;
const DEFAULT_LNG = -87.6969984;

const UNIQUE_TREASURE_TYPES = new Set(['user_unique', 'global_unique', 'character_unique']);

const Board = styled.div`
  width: 100vw;
  height: 100vh;
  margin: 0;
  padding: 0;
  position: absolute;
  inset: 0;
`;

const LoadingScreen = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors("background")};
  color: ${({ theme }) => theme.colors("foreground", 0.45)};
  font-family: 'Silkscreen', monospace;
  font-size: 1.1rem;
`;

// Renders map entities only for game logic — no visual output.
// The Canvas is the sole rendering layer.
const EntityRenderer = ({ entities }) => {
  return entities.map((entity) => {
    // Forward every entity field to the actor by default — enumerating fields
    // by hand caused silent drops in the past (treasureBehavior, npcPosition).
    const { x, y, _levelIndex, ...config } = entity;
    const { id, entityType } = config;
    config.npc = entityType === "npc";

    if (entityType === "monster")
      return <Monster key={id} x={x} y={y} width={GRID_SIZE} height={GRID_SIZE} config={config} />;
    if (entityType === "item" || entityType === "treasure")
      return <Item key={id} x={x} y={y} width={16} height={16} config={config} />;
    if (entityType === "npc")
      return <NPC key={id} x={x} y={y} width={GRID_SIZE} height={GRID_SIZE} config={config} />;
    if (entityType === "ladder")
      return <Ladder key={id} x={x} y={y} width={GRID_SIZE} height={GRID_SIZE} config={config} />;
    if (entityType === "exit")
      return <Exit key={id} x={x} y={y} width={GRID_SIZE} height={GRID_SIZE} config={config} />;
    return null;
  });
};

const Game = ({
  mapData,
  anchor,
  onRestart,
  dungeonKey,
  activeSave,
  startPos,
  beforeExitRef,
}) => {
  const mapInstance = useMapInstance();
  const {
    gameOver,
    playerPos,
    setTarget,
    currentLevel,
    health,
    score,
    xp,
    wallet,
    inventory,
    equippedWeaponId,
    equippedArmorId,
    autoDefence,
    paused,
    droppedItems,
    visitedLevels,
    markLevelVisited,
    visitedDungeons,
    markDungeonVisited,
    questProgressRef,
    respawnKey,
  } = useWorld();
  const [{ session }] = useStateValue();
  const { colors } = useTheme();
  const playerUsername = session?.common_name || session?.username || null;
  const playerSprite = activeSave?.character?.sprite ?? null;
  useGameSocket(dungeonKey);

  useEffect(() => {
    if (dungeonKey) markDungeonVisited(dungeonKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    markLevelVisited(currentLevel);
  }, [currentLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  const visitedLevelsRef = useRef(visitedLevels);
  useEffect(() => { visitedLevelsRef.current = visitedLevels; }, [visitedLevels]);
  const visitedDungeonsRef = useRef(visitedDungeons);
  useEffect(() => { visitedDungeonsRef.current = visitedDungeons; }, [visitedDungeons]);

  // Build save payload from current world state
  const buildSaveData = useCallback(() => ({
    stats: {
      ...activeSave.stats,
      health,
      score,
      xp,
      wallet,
      level: currentLevel + 1,
      visitedLevels: visitedLevelsRef.current,
      visitedDungeons: visitedDungeonsRef.current,
      equippedWeaponId,
      equippedArmorId,
      autoDefence,
      questProgress: questProgressRef.current,
    },
    inventory,
    position: playerPos
      ? { level: currentLevel, x: playerPos.x, y: playerPos.y }
      : activeSave.position,
  }), [activeSave, health, score, xp, wallet, inventory, equippedWeaponId, equippedArmorId, autoDefence, currentLevel, playerPos]);

  const saveTimeoutRef = useRef(null);
  const lastSavedSnapshotRef = useRef(null);

  const persistSave = useCallback(() => {
    if (!activeSave?.id) return;
    const saveData = buildSaveData();
    const snapshot = JSON.stringify(saveData);
    if (snapshot === lastSavedSnapshotRef.current) return;
    updateDungeonSave(activeSave.id, saveData)
      .then(() => {
        lastSavedSnapshotRef.current = snapshot;
        setActiveSave({ ...activeSave, ...saveData });
      })
      .catch((err) => {
        if (err?.status !== 404) return;
        createDungeonSave({ ...activeSave, ...saveData })
          .then((data) => {
            if (!data?.save) return;
            lastSavedSnapshotRef.current = snapshot;
            setActiveSave(data.save);
          })
          .catch(() => {});
      });
  }, [activeSave, buildSaveData]);

  useEffect(() => {
    if (!activeSave?.id) return;
    saveTimeoutRef.current = setInterval(persistSave, 30_000);
    return () => clearInterval(saveTimeoutRef.current);
  }, [persistSave]);

  useEffect(() => {
    if (activeSave?.id) persistSave();
  }, [currentLevel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!beforeExitRef) return;
    beforeExitRef.current = () => {
      if (!activeSave) return;
      const saveData = buildSaveData();
      clearPlayerPos(dungeonKey);
      setActiveSave({ ...activeSave, ...saveData });
      if (activeSave.id) {
        updateDungeonSave(activeSave.id, saveData).catch((err) => {
          if (err?.status !== 404) return;
          createDungeonSave({ ...activeSave, ...saveData })
            .then((data) => { if (data?.save) setActiveSave(data.save); })
            .catch(() => {});
        });
      }
    };
  }, [beforeExitRef, dungeonKey, activeSave, buildSaveData]);

  const levelData = mapData.levels
    ? (mapData.levels[currentLevel] ?? mapData.levels[0])
    : mapData;

  useTileRegistry(levelData.tiles, GRID_SIZE, levelData.entities || []);

  useEffect(() => {
    if (!playerPos) return;
    if (currentLevel === 0) savePlayerPos(dungeonKey, playerPos.x, playerPos.y, GRID_SIZE);
  }, [playerPos?.x, playerPos?.y]); // eslint-disable-line react-hooks/exhaustive-deps

  const pausedRef = useRef(paused);
  useEffect(() => { pausedRef.current = paused; }, [paused]);
  useEffect(() => {
    if (!mapInstance) return;
    const handleClick = (e) => {
      if (pausedRef.current) return;
      const { gx, gy } = latLngToGamePixel(anchor.lat, anchor.lng, e.latlng.lat, e.latlng.lng);
      setTarget({ x: gx, y: gy });
    };
    mapInstance.on("click", handleClick);
    return () => mapInstance.off("click", handleClick);
  }, [mapInstance, anchor]);

  return (
    <>
      <Canvas
        tiles={levelData.tiles}
        decorationTiles={levelData.decorationTiles || null}
        overlayTiles={levelData.overlayTiles || null}
        visibility={levelData.visibility ?? "bright"}
        entities={levelData.entities || []}
        gridSize={GRID_SIZE}
        anchor={anchor}
      />

      <div style={{ position: "absolute", opacity: 0, pointerEvents: "none", zIndex: -1 }}>
        <EntityRenderer
          key={`${currentLevel}-${respawnKey}`}
          entities={levelData.entities || []}
        />
        {droppedItems.filter((item) => item.level == null || item.level === currentLevel).map((item) => (
          <Item
            key={item.id}
            x={item.x}
            y={item.y}
            width={16}
            height={16}
            config={{
              id: item.id,
              entityType: "item",
              value: item.value,
              treasureType: item.type,
              name: item.name,
              dropped: true,
              treasureBehavior: "normal",
              illumination: item.illumination || 0,
              lifespan: item.lifespan || 0,
            }}
          />
        ))}
        <Player
          x={startPos.x}
          y={startPos.y}
          width={GRID_SIZE}
          height={GRID_SIZE}
          config={{
            id: "player",
            backgroundColor: colors("accent"),
            username: playerUsername,
            sprite: playerSprite,
            stride: activeSave?.stats?.speed ?? 3,
          }}
        />
      </div>

      <HUD dungeonKey={dungeonKey} />
      <LadderChoice />
      <Terminal />
      {gameOver && <GameOver onRestart={onRestart} />}
    </>
  );
};

const GameBoard = ({
  mapData,
  quests,
  questProgress,
  anchor,
  onRestart,
  dungeonKey,
  activeSave,
  session,
  onExit,
  collectTreasure,
  killMonster,
  onItemDrop,
  onItemPickup,
}) => {
  const beforeExitRef = useRef(null);
  const handleExitWithSave = useCallback(() => {
    beforeExitRef.current?.();
    onExit();
  }, [onExit]);

  const startPos = useMemo(
    () => computeStartPos(mapData, dungeonKey, activeSave),
    [dungeonKey] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const startCenter = useMemo(
    () => gamePixelToLatLng(anchor.lat, anchor.lng, startPos.x, startPos.y),
    [anchor, startPos]
  );

  return (
    <Board>
      <LeafletMap
        center={startCenter}
        zoom={18}
        tileUrl={null}
        options={{ dragging: false, scrollWheelZoom: false, doubleClickZoom: false, boxZoom: false, keyboard: false, tap: false }}
      >
        <WorldProvider
          initialStats={
            activeSave
              ? { ...activeSave.stats, inventory: activeSave.inventory, equippedWeaponId: activeSave.stats?.equippedWeaponId ?? null, equippedArmorId: activeSave.stats?.equippedArmorId ?? null, questProgress: questProgress }
              : {}
          }
          quests={quests}
          dungeonSlug={dungeonKey}
          session={session}
          saveId={activeSave?.id ?? null}
          onExit={handleExitWithSave}
          collectTreasure={collectTreasure}
          killMonster={killMonster}
          onItemDrop={onItemDrop}
          onItemPickup={onItemPickup}
        >
          <Game
            mapData={mapData}
            anchor={anchor}
            onRestart={onRestart}
            dungeonKey={dungeonKey}
            activeSave={activeSave}
            beforeExitRef={beforeExitRef}
            collectTreasure={collectTreasure}
            startPos={startPos}
          />
        </WorldProvider>
      </LeafletMap>
    </Board>
  );
};

export const Pinmonkey = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [restartKey, setRestartKey] = useState(0);
  const [{ session }] = useStateValue();
  const [sessionCollected, setSessionCollected] = useState(() => new Set());
  const { colors } = useTheme();

  const dungeonKey = slug || "default";
  const [activeSave, setActiveSaveState] = useState(() => getActiveSave());
  const saveId = activeSave?.id ?? null;

  const { data, isFetching: loading, error } = useGetQuery({
    url: slug ? `/dungeons/${slug}${saveId ? `?save_id=${saveId}` : ""}` : null,
    enabled: !!slug,
  });

  const handleRestart = () => {
    clearPlayerPos(dungeonKey);
    setRestartKey((k) => k + 1);
  };

  const handleExit = useCallback(() => {
    navigate("/games/dungeon");
  }, [navigate]);

  useEffect(() => {
    const pending = loadPendingCollections();
    if (!pending.length) return;
    pending.forEach(({ dungeonSlug, treasureId, treasureType, saveId: sid }) => {
      collectUniqueTreasure(dungeonSlug, treasureId, treasureType, sid)
        .then(() => removePendingCollection(treasureId, dungeonSlug))
        .catch(() => {});
    });
  }, []);

  const handleCollectTreasure = useCallback(
    (treasureId, treasureType) => {
      if (!slug) return;
      if (UNIQUE_TREASURE_TYPES.has(treasureType)) {
        setSessionCollected((prev) => new Set([...prev, treasureId]));
      }
      collectUniqueTreasure(slug, treasureId, treasureType, activeSave?.id).catch(() => {
        if (UNIQUE_TREASURE_TYPES.has(treasureType)) {
          addPendingCollection({ dungeonSlug: slug, treasureId, treasureType, saveId: activeSave?.id ?? null });
        }
      });
    },
    [slug, activeSave?.id]
  );

  const handleMonsterKill = useCallback(
    (monsterId) => {
      if (!slug) return;
      recordMonsterKill(slug, monsterId).catch(() => {});
    },
    [slug]
  );

  const socket = useSocket();
  const handleItemDrop = useCallback(
    (item) => {
      if (!socket || !slug) return;
      socket.emit("dungeon:item_drop", { dungeonId: slug, item });
    },
    [socket, slug]
  );

  const handleItemPickup = useCallback(
    (id) => {
      if (!socket || !slug) return;
      socket.emit("dungeon:item_pickup", { dungeonId: slug, id });
    },
    [socket, slug]
  );

  if (slug && loading && !data?.dungeon?.map_data)
    return <LoadingScreen>Loading dungeon…</LoadingScreen>;
  if (slug && error)
    return <LoadingScreen>Could not load dungeon.</LoadingScreen>;

  if (!activeSave) {
    return (
      <CharacterSelect
        onEnter={(save) => setActiveSaveState(save)}
        onCancel={() => navigate("/games/dungeon")}
      />
    );
  }

  const rawMapData = data?.dungeon?.map_data;
  if (!rawMapData) return <LoadingScreen>Loading dungeon…</LoadingScreen>;

  const hiddenTreasures = new Set([...(data?.dungeon?.hidden_treasure_ids ?? []), ...sessionCollected]);
  const hiddenMonsters = new Set(data?.dungeon?.hidden_monster_ids ?? []);

  const quests = (data?.dungeon?.quests ?? rawMapData?.quests ?? []).map((q) => ({
    id: q.id,
    name: q.name,
    description: q.description,
    steps: q.steps ?? [],
    award: q.award ?? null,
    dialog: q.dialog ?? {},
    giverNpcPlacementId: q.giverNpcPlacementId ?? q.giver_npc_placement_id ?? null,
  }));

  const serverProgress = (data?.dungeon?.quest_progress ?? []).reduce((acc, p) => {
    acc[p.quest_id] = { status: p.status, completedSteps: p.completed_steps ?? [] };
    return acc;
  }, {});
  const questProgress = { ...(activeSave?.stats?.questProgress ?? {}), ...serverProgress };

  const dungeonTimezone = data?.dungeon?.timezone ?? null;
  // All NPC placements are included regardless of schedule — the NPC component
  // starts hidden when its boundary is inactive and un-hides on hour change.
  const npcPlacementEntities = (data?.dungeon?.npc_placements ?? [])
    .map((p) => ({
      id: p.placement_id,
      x: p.x,
      y: p.y,
      width: p.width ?? GRID_SIZE,
      height: p.height ?? GRID_SIZE,
      entityType: "npc",
      backgroundColor: p.background_color || colors("accent"),
      name: p.name,
      sprite: p.sprite,
      stationary: p.stationary,
      boundary: p.boundary,
      boundaryPeriod: p.boundary_period,
      timezone: dungeonTimezone,
      interactionAreas: p.interaction_areas,
      dialogs: p.dialogs,
      inventory: p.inventory,
      _levelIndex: p.level_index,
    }));

  const mapData = (() => {
    const storedGrid = rawMapData.grid || GRID_SIZE;
    const posScale = storedGrid !== GRID_SIZE ? GRID_SIZE / storedGrid : 1;
    const scalePos = (e) =>
      posScale === 1 ? e : { ...e, x: Math.round(e.x * posScale), y: Math.round(e.y * posScale) };

    const filterEntities = (entities, levelIndex) => {
      const levelNpcs = npcPlacementEntities.filter((e) => e._levelIndex === levelIndex).map(scalePos);
      return [
        ...(entities || []).filter((e) => {
          if (e.entityType === "npc") return false;
          if ((e.entityType === "item" || e.entityType === "treasure") && hiddenTreasures.has(e.id)) return false;
          if (e.entityType === "monster" && hiddenMonsters.has(e.id)) return false;
          return true;
        }).map(scalePos),
        ...levelNpcs,
      ];
    };

    if (rawMapData.levels) {
      return {
        ...rawMapData,
        grid: GRID_SIZE,
        levels: rawMapData.levels.map((l, i) => ({ ...l, entities: filterEntities(l.entities, i) })),
      };
    }
    return { ...rawMapData, grid: GRID_SIZE, entities: filterEntities(rawMapData.entities, 0) };
  })();

  const anchor = {
    lat: data?.dungeon?.lat ?? DEFAULT_LAT,
    lng: data?.dungeon?.lng ?? DEFAULT_LNG,
  };

  return (
    <GameBoard
      key={restartKey}
      mapData={mapData}
      quests={quests}
      questProgress={questProgress}
      anchor={anchor}
      onRestart={handleRestart}
      onExit={handleExit}
      collectTreasure={slug ? handleCollectTreasure : null}
      killMonster={slug ? handleMonsterKill : null}
      onItemDrop={slug ? handleItemDrop : null}
      onItemPickup={slug ? handleItemPickup : null}
      dungeonKey={dungeonKey}
      activeSave={activeSave}
      session={session}
    />
  );
};
